/**
 * Sites slice (SCRUM-596) — the createSlice/createAsyncThunk pilot of the RTK
 * track. State is typed from the shared server/client contracts
 * (shared/apiTypes.js), so the ~50 useSelector call-sites reading this slice
 * are field-name checked. The legacy import path `./siteActions` re-binds
 * everything exported here; new code should import from this module.
 *
 * Patterns established here for the pages migration (SCRUM-722):
 * - Cross-slice error coupling: every thunk's catch keeps dispatching the plain
 *   GET_ERRORS action with the same payload the legacy siteActions sent, so the
 *   legacy errors slice (GlobalNotifications hub) is fed unchanged. The slice's
 *   own `error` field rides that same action via extraReducers. rejectWithValue
 *   carries the *original* error so `.unwrap()` reproduces the legacy rethrow
 *   contract (callers read err.response.data off it).
 * - Cross-slice state coupling: legacy type strings this slice reacts to or
 *   emits (ADD_PENDING_PAGES, the app-wide shared CLEAR_MESSAGE) are handled
 *   via extraReducers.addCase on the legacy string.
 * - Legacy call signatures that don't fit the single-arg thunk API are kept as
 *   thin wrappers around the createAsyncThunk (getCurrentSite, and the two
 *   thunks whose callers rely on the promise rejecting: updateSettingVariables,
 *   deleteSettingsVariables).
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { Action, PayloadAction, ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";
import type {
  SitesResponse,
  SiteByIdResponse,
  CurrentSiteResponse,
  MessageResponse,
  SiteSelectorSitesResponse,
  ConfiguredSettingVariablesResponse,
  SiteSettingsVariablesWriteResponse,
  LeanSite,
  LeanPage,
  SiteRecoverResponse,
} from "../../../../shared/apiTypes";
import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { asCaught, errorPayload } from "../caughtError";
import type { AppDispatch } from "../store";

// Shared legacy string, not a slice-scoped type: 11 other legacy modules define
// the same "CLEAR_MESSAGE" value and their clearMessage creators dispatch it,
// clearing `message` in every slice at once (and site containers' clearMessage
// clears theirs). Both directions are kept: the slice listens to the plain
// string via extraReducers, and the exported clearMessage still emits it.
const CLEAR_MESSAGE = "CLEAR_MESSAGE";

/**
 * Client-side ids are strings after JSON transport; the contracts type them as
 * ObjectId. Page-mutation payloads accept both.
 */
export type PageId = NonNullable<LeanPage["_id"]> | string;

/**
 * The current-site shape the client actually holds: `required_pages` populated
 * with LeanPage docs, never raw ObjectId refs, and no `pages` at all — a site's
 * pages are read from GET /api/pages (SCRUM-1812; see CurrentSiteResponse in
 * shared/apiTypes.js).
 */
export type CurrentSite = Omit<LeanSite, "pages" | "required_pages"> & {
  required_pages?: LeanPage[];
};

export type SitesState = {
  sites: SitesResponse;
  message: string;
  site: CurrentSite;
  siteToUpdate: SiteByIdResponse;
  loading: boolean;
  SettingsVariables: NonNullable<ConfiguredSettingVariablesResponse["settings_variables"]>;
  siteselector_sites: SiteSelectorSitesResponse;
  pendingpage: Record<string, never>;
  idPendingPage: string;
  error: unknown;
};

const initialState: SitesState = {
  sites: [],
  message: "",
  site: {},
  siteToUpdate: {},
  loading: false,
  // SCRUM-1225: SettingsRequired / SettingsOptional held the design-level
  // catalogue and were never read by any screen. Gone with the route that
  // filled them. SettingsVariables below is the site's own configured list
  // and stays.
  SettingsVariables: [],
  siteselector_sites: [],
  // Cleared by clearPendingPageInfo but never populated — dead pair kept for
  // behaviour parity (SCRUM-981-class follow-up).
  pendingpage: {},
  idPendingPage: "",
  // Written by every GET_ERRORS (any module's); nothing reads it today — the
  // notification hub reads the separate `errors` slice.
  error: null,
};

// ---------------------------------------------------------------------------
// Async thunks. Each catch mirrors its legacy siteActions block: the GET_ERRORS
// payload is kept identical per action (some were guarded, some not), and
// rejectWithValue carries the original error for unwrap()-style callers.
// ---------------------------------------------------------------------------

// Get Sites
export const getSites = createAsyncThunk(
  "sites/getSites",
  async (_arg: void, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<SitesResponse> = await api.get("/api/sites");
      return res.data;
    } catch (err) {
      // Legacy parity: getSites sent the raw error to the hub, not response.data.
      dispatch({ type: GET_ERRORS, payload: err });
      return rejectWithValue(err);
    }
  }
);

// Add Site
export const addSite = createAsyncThunk(
  "sites/addSite",
  async (siteData: LeanSite, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<MessageResponse> = await api.post(
        "/api/sites",
        siteData
      );
      return res.data;
    } catch (err) {
      dispatch({ type: GET_ERRORS, payload: errorPayload(err) });
      return rejectWithValue(err);
    }
  }
);

// Delete Site — the server answers 200 without a body; the legacy DELETE_SITE
// action had no reducer anywhere, so only the getSites refresh is kept.
// createAppAsyncThunk (SCRUM-1021 follow-up): a failed DELETE now rejects and is
// surfaced once centrally by appErrorMiddleware, and `.unwrap()` lets the caller
// gate its success toast — it used to dispatch no error at all, so the delete
// screen reported a false success on failure.
export const deleteSite = createAppAsyncThunk(
  "sites/deleteSite",
  async (id: PageId, { dispatch }) => {
    await api.delete<unknown>(`/api/sites/${id}`);
    dispatch(getSites());
    return id;
  }
);

// SCRUM-1817: the two answers to the 409 addSite gets when only a deleted site
// holds the domain or canonical URL. Both surface a failure through the hub
// (GET_ERRORS) and reject, so the form can keep its offer on screen; `.unwrap()`
// re-throws the original axios error.

// Restore: bring the deleted site back, with its pages, under its reserved values.
export const restoreSite = createAsyncThunk(
  "sites/restoreSite",
  async (id: PageId, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<SiteRecoverResponse> = await api.post(`/api/sites/${id}/restore`);
      dispatch(getSites());
      return res.data;
    } catch (err) {
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

// Start fresh: release the deleted site's domain and canonical URL for a new site.
export const releaseSite = createAsyncThunk(
  "sites/releaseSite",
  async (id: PageId, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<SiteRecoverResponse> = await api.post(`/api/sites/${id}/release`);
      return res.data;
    } catch (err) {
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

// Get Site
export const getSite = createAsyncThunk(
  "sites/getSite",
  async (id: PageId, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<SiteByIdResponse> = await api.get(
        `/api/sites/${id}`
      );
      return res.data;
    } catch (err) {
      // SCRUM-856: surface the real error via the hub instead of nulling
      // siteToUpdate and swallowing the failure.
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

// Edit Site
export const EditSite = createAsyncThunk(
  "sites/EditSite",
  async (site: LeanSite, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<MessageResponse> = await api.put(
        `/api/sites/${site._id}`,
        site
      );
      dispatch(getSite(site._id as PageId));
      return res.data;
    } catch (err) {
      dispatch({ type: GET_ERRORS, payload: errorPayload(err) });
      return rejectWithValue(err);
    }
  }
);

// Get Siteselector's sites
export const getSitesForSiteselector = createAsyncThunk(
  "sites/getSitesForSiteselector",
  async (iduser: string, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<SiteSelectorSitesResponse> = await api.get(
        `/api/sites/siteselector/${iduser}`
      );
      return res.data;
    } catch (err) {
      // SCRUM-856: route the real error to the hub instead of nulling the
      // current site (the legacy catch dispatched the wrong action with null).
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

// SCRUM-1225: the `getSettingVariables` thunk stood here, fetching
// /api/sites/getsettingvariables — the design-level catalogue. Its result was
// stored and never rendered, and its reducer case read
// `action.payload.settings_variables.SettingsRequired` unguarded, so it was also
// the crash path once the field went. Removed with the field.
//
// `getSettingVariablesToUpdate` below is a different thunk on a different route
// and stays: it reads site.settings_variables, which is live.

// Get setting variables to update
export const getSettingVariablesToUpdate = createAsyncThunk(
  "sites/getSettingVariablesToUpdate",
  async (_arg: void, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<ConfiguredSettingVariablesResponse> = await api.get(
        "/api/sites/getconfiguredsettingvariables"
      );
      return res.data;
    } catch (err) {
      // SCRUM-856: carry the real error to the hub instead of a null payload it ignores.
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

type SettingVariablesWrite = { settings_variables: any[] }; // any: the rows are edited free-form by the site configuration screen; no shared row type exists yet

const updateSettingVariablesThunk = createAsyncThunk(
  "sites/updateSettingVariables",
  async (data: SettingVariablesWrite, { dispatch, rejectWithValue }) => {
    try {
      await api.put<SiteSettingsVariablesWriteResponse>("/api/sites/updatesettingvariables", data);
      dispatch(getSettingVariablesToUpdate());
    } catch (err) {
      // SCRUM-856: carry the real error to the hub instead of a null payload it ignores.
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

/**
 * Update setting variables. Legacy contract: the returned promise rejects with
 * the original error (UpdateSiteConfigurationsContainer reads
 * err.response.data off it), hence the unwrap() wrapper.
 */
export const updateSettingVariables = (data: SettingVariablesWrite) => (dispatch: AppDispatch) =>
  dispatch(updateSettingVariablesThunk(data)).unwrap();

const deleteSettingsVariablesThunk = createAsyncThunk(
  "sites/deleteSettingsVariables",
  async (_arg: void, { dispatch, rejectWithValue }) => {
    try {
      const res: AxiosResponse<SiteSettingsVariablesWriteResponse> = await api.put(
        "/api/sites/deletesettingvariables"
      );
      dispatch(getSettingVariablesToUpdate());
      return res.data;
    } catch (err) {
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
      return rejectWithValue(err);
    }
  }
);

/**
 * Delete settings variables. Legacy contract: resolves with res.data and
 * rejects with the original error, hence the unwrap() wrapper.
 */
export const deleteSettingsVariables = () => (dispatch: AppDispatch) =>
  dispatch(deleteSettingsVariablesThunk()).unwrap();

/**
 * Get the current site: one request, one currentSiteReceived, loading on and
 * off around it. SCRUM-1812: the response carries no pages any more, so the
 * batch loop that reassembled them 50 at a time is gone; a site's pages are
 * read from GET /api/pages (SCRUM-1811).
 */
const getCurrentSiteThunk = createAsyncThunk(
  "sites/getCurrentSite",
  async (_arg: void, { dispatch }) => {
    const { setLoading, currentSiteReceived } = sitesSlice.actions;

    dispatch(setLoading(true));

    try {
      const res: AxiosResponse<CurrentSiteResponse> = await api.get("/api/sites/searchByDomain/domain");

      dispatch(currentSiteReceived(res.data.currentSite));
      dispatch(setLoading(false));
    } catch (err) {
      dispatch({
        type: GET_ERRORS,
        payload: asCaught(err).response
          ? asCaught(err).response?.data
          : { message: "An error occurred while fetching the site data" },
      });
      dispatch(setLoading(false)); // Stop loading on error
    }
  }
);

/**
 * Legacy two-argument signature kept as a wrapper for its callers; both
 * arguments are ignored since SCRUM-1812 (one request, no pages to page).
 */
export const getCurrentSite = (_page: unknown = 1, _limit: number = 50) =>
  getCurrentSiteThunk();

// The dispatch a thunk payload creator holds (state and extra argument unknown), so
// this one can be dispatched from inside createAsyncThunk bodies as well as from screens.
export type PayloadCreatorDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

export const getSitesByDomain = (page: unknown = 1, limit: number = 50) => (
  dispatch: PayloadCreatorDispatch
) => {
  dispatch(getCurrentSite(page, limit));
};

// Cross-slice by design (see the CLEAR_MESSAGE constant above): stays a plain
// action on the shared string so the other 11 message-carrying slices keep
// being cleared, and the sites slice picks it up via extraReducers.
export const clearMessage = () => ({ type: CLEAR_MESSAGE });

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const sitesSlice = createSlice({
  name: "sites",
  initialState,
  // The payload-carrying reducers use the prepare form, so the generated action
  // creator takes the payload type below and a wrongly-typed dispatch
  // (currentSiteReceived(42)) does not typecheck.
  reducers: {
    currentSiteReceived: {
      /**
       * One merged batch of the current site, dispatched per page by the
       * getCurrentSite flow (also the seeding point integration tests use).
       */
      reducer(state, action: PayloadAction<CurrentSite>) {
        state.site = action.payload;
      },
      prepare(payload: CurrentSite) {
        return { payload };
      },
    },
    setLoading: {
      reducer(state, action: PayloadAction<boolean>) {
        state.loading = action.payload;
      },
      prepare(payload: boolean) {
        return { payload };
      },
    },
    deletePageFromSite: {
      reducer(state, action: PayloadAction<PageId>) {
        // SCRUM-1812: the site holds only its chrome pages; the Custom Pages
        // table refetches its own page (refetchPageList, SCRUM-1811).
        state.site.required_pages =
          state.site.required_pages?.filter(
            (page) => page._id !== action.payload
          ) || [];
      },
      prepare(id: PageId) {
        return { payload: id };
      },
    },
    clearPendingPageInfo(state) {
      state.pendingpage = {};
      state.idPendingPage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Cross-slice: the shared CLEAR_MESSAGE string every legacy
      // message-carrying slice listens to (see the constant above).
      .addCase(CLEAR_MESSAGE, (state) => {
        state.message = "";
      })
      .addCase(getSites.fulfilled, (state, action) => {
        state.sites = action.payload;
      })
      .addCase(getSite.fulfilled, (state, action) => {
        state.siteToUpdate = action.payload;
      })
      .addCase(addSite.fulfilled, (state, action) => {
        state.message = action.payload.msg;
      })
      .addCase(EditSite.fulfilled, (state, action) => {
        state.message = action.payload.msg;
      })
      .addCase(getSitesForSiteselector.fulfilled, (state, action) => {
        state.siteselector_sites = action.payload;
      })
      .addCase(getSettingVariablesToUpdate.fulfilled, (state, action) => {
        state.SettingsVariables = action.payload.settings_variables ?? [];
      })
      .addCase(deleteSettingsVariablesThunk.fulfilled, (state) => {
        state.SettingsVariables = [];
      })
      // SCRUM-1225: ADD_PENDING_PAGE used to clear SettingsRequired /
      // SettingsOptional here — legacy parity for two fields that are gone.
      // Nothing else was cleared on that action, so the case goes with them.
      //
      // Cross-slice: any module's GET_ERRORS also lands here (legacy parity;
      // unread today — the notification hub reads the errors slice).
      .addCase(GET_ERRORS, (state, action: Action<typeof GET_ERRORS> & { payload?: unknown }) => {
        state.error = action.payload;
      });
  },
});

export const {
  currentSiteReceived,
  setLoading,
} = sitesSlice.actions;

// Legacy public name for the page-deletion creator (pagesSlice dispatches it).
export const DeletePageFromSite = sitesSlice.actions.deletePageFromSite;

// Exposed for tests that assert on the async action lifecycle.
export { deleteSettingsVariablesThunk };

export { initialState };
export default sitesSlice.reducer;
